/**
 * /accept-invite — PUBLIC route.
 *
 * Reads ?token= from the URL, calls the public preview endpoint to learn
 * about the invitation, then renders the appropriate state:
 *
 *  • loading              — skeleton
 *  • invalid/expired      — friendly error card + link to /login
 *  • logged in, match     — accept & join card
 *  • logged in, mismatch  — wrong-account card + log out
 *  • logged out, has acct — log in first card (preserves return URL)
 *  • logged out, new user — register-and-accept form
 *
 * On-brand: amber accent, inviter/tenant context prominent.
 */

import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useId, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Building2, UserCheck, AlertCircle, LogOut } from "lucide-react";
import { Button, Skeleton } from "@teamblob/ui/components";
import { ApiError } from "@teamblob/api-client";
import { useAuthStore, useCurrentUser, useIsAuthenticated } from "../stores/auth-store.js";
import { useBodyClass } from "../hooks/use-body-class.js";
import { setCsrfToken } from "@teamblob/api-client";
import { usePreviewInvitation, useAcceptInvitation, useRegisterAndAccept, useSetPasswordAndAccept } from "../hooks/useInvitations.js";
import { useSetActiveWorkspace } from "../hooks/useWorkspaces.js";
import { useToast } from "../components/ui/toast.js";
import { AuthField, AuthErrorBanner } from "../components/desktop/auth-primitives.js";
import type { InvitationMembershipType } from "../lib/invitations-api.js";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

const searchSchema = z.object({
  token: z.string().default(""),
});

export const Route = createFileRoute("/accept-invite")({
  validateSearch: searchSchema,
  component: AcceptInvitePage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Human-readable label for a membershipType. */
function membershipLabel(type: InvitationMembershipType | undefined): string {
  switch (type) {
    case "member":           return "Team member";
    case "guest_client":     return "Client";
    case "guest_consultant": return "Consultant";
    case "guest_editor":     return "Editor";
    case "guest_freelancer": return "Freelancer";
    case "guest_custom":     return "Custom guest";
    default:                 return "Member";
  }
}

// ---------------------------------------------------------------------------
// Register-and-accept form schema
// ---------------------------------------------------------------------------

const registerSchema = z.object({
  name: z.string().min(1, "Name is required."),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters.")
    .max(128, "Password must be at most 128 characters."),
});

type RegisterFormData = z.infer<typeof registerSchema>;

// ---------------------------------------------------------------------------
// Shell — shared card wrapper
// ---------------------------------------------------------------------------

function InviteCard({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-window-face)] shadow-[var(--shadow-window)]">
        {/* Amber accent strip */}
        <div className="h-1 w-full rounded-t-[var(--radius-lg)] bg-[var(--color-accent-amber)]" />
        <div className="px-8 py-7">{children}</div>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Loading state
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <InviteCard>
      <div className="space-y-4">
        <Skeleton className="h-5 w-3/5 rounded" />
        <Skeleton className="h-4 w-4/5 rounded" />
        <Skeleton className="h-4 w-2/3 rounded" />
        <Skeleton className="mt-4 h-9 w-full rounded" />
      </div>
    </InviteCard>
  );
}

// ---------------------------------------------------------------------------
// Invalid invitation state
// ---------------------------------------------------------------------------

function InvalidInvite({ reason }: { reason: string }) {
  return (
    <InviteCard>
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-text-danger)]/10">
        <AlertCircle size={20} className="text-[var(--color-text-danger)]" aria-hidden="true" />
      </div>
      <h1 className="mb-2 text-[18px] font-semibold text-[var(--color-text-strong)]">
        This invitation is no longer valid
      </h1>
      <p className="mb-6 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
        {reason}
      </p>
      <Link
        to="/login"
        className="inline-flex items-center text-[13px] font-semibold text-[var(--color-accent-amber-dark)] hover:underline"
      >
        Go to sign in
      </Link>
    </InviteCard>
  );
}

// ---------------------------------------------------------------------------
// Context header — shown in all valid states
// ---------------------------------------------------------------------------

function InviteContext({
  inviterName,
  tenantName,
  roleName,
  membershipType,
}: {
  inviterName: string | undefined;
  tenantName: string | undefined;
  roleName: string | undefined;
  membershipType: InvitationMembershipType | undefined;
}) {
  const roleLabel = roleName ?? membershipLabel(membershipType);
  return (
    <div className="mb-6">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-accent-amber)]/15">
        <Building2
          size={18}
          className="text-[var(--color-accent-amber-dark)]"
          aria-hidden="true"
        />
      </div>
      {inviterName !== undefined && tenantName !== undefined ? (
        <p className="mb-1 text-[13px] text-[var(--color-text-muted)]">
          <strong className="font-semibold text-[var(--color-text-strong)]">
            {inviterName}
          </strong>{" "}
          invited you to join
        </p>
      ) : (
        <p className="mb-1 text-[13px] text-[var(--color-text-muted)]">
          You&apos;ve been invited to join
        </p>
      )}
      <h1 className="mb-1 text-[22px] font-semibold text-[var(--color-text-strong)]">
        {tenantName ?? "a workspace"}
      </h1>
      <span className="inline-flex rounded-full bg-[var(--color-accent-amber)]/15 px-2.5 py-0.5 text-[11px] font-semibold text-[var(--color-accent-amber-dark)]">
        {roleLabel}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// State: logged in + email matches → accept & join
// ---------------------------------------------------------------------------

function AcceptAndJoin({
  token,
  preview,
}: {
  token: string;
  preview: {
    tenantName?: string | undefined;
    inviterName?: string | undefined;
    roleName?: string | undefined;
    membershipType?: InvitationMembershipType | undefined;
  };
}) {
  const navigate = useNavigate();
  const accept = useAcceptInvitation();
  const setActiveWorkspace = useSetActiveWorkspace();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);

  const handleAccept = () => {
    setError(null);
    accept.mutate(
      { token },
      {
        onSuccess: async (res) => {
          const { tenantId, tenantName } = res.data;
          toast({
            variant: "success",
            title: `Joined ${tenantName}`,
            description: "Switching to your new workspace…",
          });
          // Switch the active workspace so the user lands in the right tenant.
          setActiveWorkspace.mutate(tenantId, {
            onSettled: () => {
              void navigate({ to: "/" });
            },
          });
        },
        onError: (err) => {
          setError(err.message ?? "Failed to accept the invitation. Please try again.");
        },
      },
    );
  };

  const isPending = accept.isPending || setActiveWorkspace.isPending;

  return (
    <InviteCard>
      <InviteContext
        inviterName={preview.inviterName}
        tenantName={preview.tenantName}
        roleName={preview.roleName}
        membershipType={preview.membershipType}
      />

      {error !== null && (
        <div className="mb-4">
          <AuthErrorBanner message={error} />
        </div>
      )}

      <Button
        variant="primary"
        size="lg"
        fullWidth
        isLoading={isPending}
        disabled={isPending}
        onClick={handleAccept}
        leftIcon={<UserCheck size={14} />}
      >
        {isPending ? "Joining…" : "Accept & join workspace"}
      </Button>
    </InviteCard>
  );
}

// ---------------------------------------------------------------------------
// State: logged in + email mismatch
// ---------------------------------------------------------------------------

function WrongAccount({
  inviteEmail,
  currentEmail,
}: {
  inviteEmail: string;
  currentEmail: string;
}) {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    void navigate({ to: "/login" });
  };

  return (
    <InviteCard>
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-text-warning)]/10">
        <AlertCircle size={20} className="text-[var(--color-text-warning)]" aria-hidden="true" />
      </div>
      <h1 className="mb-2 text-[18px] font-semibold text-[var(--color-text-strong)]">
        Wrong account
      </h1>
      <p className="mb-1 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
        This invitation was sent to{" "}
        <strong className="font-semibold text-[var(--color-text-strong)]">
          {inviteEmail}
        </strong>
        , but you&apos;re currently signed in as{" "}
        <strong className="font-semibold text-[var(--color-text-strong)]">
          {currentEmail}
        </strong>
        .
      </p>
      <p className="mb-6 text-[13px] text-[var(--color-text-muted)]">
        Sign out and sign in with the invited address to accept.
      </p>
      <Button
        variant="secondary"
        size="md"
        isLoading={loggingOut}
        disabled={loggingOut}
        onClick={() => void handleLogout()}
        leftIcon={<LogOut size={14} />}
      >
        {loggingOut ? "Signing out…" : "Sign out"}
      </Button>
    </InviteCard>
  );
}

// ---------------------------------------------------------------------------
// State: logged out + alreadyHasAccount → log in first
// ---------------------------------------------------------------------------

function LogInFirst({
  tenantName,
  inviterName,
  roleName,
  membershipType,
  returnUrl,
}: {
  tenantName: string | undefined;
  inviterName: string | undefined;
  roleName: string | undefined;
  membershipType: InvitationMembershipType | undefined;
  returnUrl: string;
}) {
  const navigate = useNavigate();
  return (
    <InviteCard>
      <InviteContext
        inviterName={inviterName}
        tenantName={tenantName}
        roleName={roleName}
        membershipType={membershipType}
      />
      <p className="mb-6 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
        You already have a TeamBlob account. Sign in to accept this invitation
        and join{" "}
        <strong className="font-semibold text-[var(--color-text-strong)]">
          {tenantName ?? "the workspace"}
        </strong>
        .
      </p>
      <Button
        variant="primary"
        size="lg"
        fullWidth
        onClick={() =>
          void navigate({
            to: "/login",
            search: { next: returnUrl },
          })
        }
      >
        Sign in to accept
      </Button>
    </InviteCard>
  );
}

// ---------------------------------------------------------------------------
// State: logged out + no account → register-and-accept form
// ---------------------------------------------------------------------------

function RegisterAndAcceptForm({
  token,
  inviteEmail,
  preview,
  mode = "register",
}: {
  token: string;
  inviteEmail: string;
  preview: {
    tenantName?: string | undefined;
    inviterName?: string | undefined;
    roleName?: string | undefined;
    membershipType?: InvitationMembershipType | undefined;
  };
  /**
   * "register" → brand-new user (register-and-accept).
   * "set-password" → existing passwordless account, e.g. a provisioned
   *   employee, who just needs to set a password (set-password-and-accept).
   */
  mode?: "register" | "set-password";
}) {
  const navigate = useNavigate();
  const registerAndAccept = useRegisterAndAccept();
  const setPasswordAndAccept = useSetPasswordAndAccept();
  const activeMutation =
    mode === "set-password" ? setPasswordAndAccept : registerAndAccept;
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const passwordToggleId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setServerError(null);
    try {
      const res = await activeMutation.mutateAsync({
        token,
        name: data.name,
        password: data.password,
      });
      // The response sets a session cookie + csrfToken — push into auth store.
      const authStore = useAuthStore.getState();
      const tenantName = res.data.tenant.name;
      setCsrfToken(res.csrfToken ?? null);
      // checkAuth reads the now-set session cookie and populates the store.
      authStore.checkAuth().then(() => {
        toast({
          variant: "success",
          title: `Welcome to ${tenantName}!`,
          description: "Your account has been created and you're all set.",
        });
        void navigate({ to: "/" });
      }).catch(() => {
        // checkAuth failed but cookie is set — navigate anyway; shell will retry.
        toast({
          variant: "success",
          title: `Welcome to ${tenantName}!`,
        });
        void navigate({ to: "/" });
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.statusCode === 409) {
          setServerError(
            "An account with this email already exists. Please sign in instead.",
          );
        } else if (err.statusCode === 400 || err.statusCode === 403) {
          setServerError(
            err.message ?? "This invitation link is invalid or has expired.",
          );
        } else {
          setServerError(err.message ?? "Something went wrong. Please try again.");
        }
      } else {
        setServerError("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <InviteCard>
      <InviteContext
        inviterName={preview.inviterName}
        tenantName={preview.tenantName}
        roleName={preview.roleName}
        membershipType={preview.membershipType}
      />

      <p className="mb-4 text-[13px] text-[var(--color-text-muted)]">
        {mode === "set-password" ? "Set a password to access " : "Create a password to join "}
        <strong className="font-semibold text-[var(--color-text-strong)]">
          {preview.tenantName ?? "the workspace"}
        </strong>
        .
      </p>

      {serverError !== null && (
        <div className="mb-4">
          <AuthErrorBanner message={serverError} />
        </div>
      )}

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        className="space-y-4"
        noValidate
      >
        {/* Invited email — read-only */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-semibold text-[var(--color-text-strong)]">
            Email
          </label>
          <input
            type="email"
            readOnly
            value={inviteEmail}
            className="w-full cursor-not-allowed rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-canvas)] px-3 py-2 text-[14px] text-[var(--color-text-muted)] outline-none"
            aria-label="Invited email address (read-only)"
          />
        </div>

        {/* Name */}
        <AuthField
          label="Your name"
          type="text"
          autoComplete="name"
          placeholder="Jane Smith"
          error={errors.name?.message}
          {...register("name")}
        />

        {/* Password */}
        <AuthField
          label="Password"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          placeholder="8+ characters"
          error={errors.password?.message}
          suffix={
            <button
              type="button"
              id={passwordToggleId}
              onClick={() => setShowPassword((v) => !v)}
              className="flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text-body)]"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          }
          {...register("password")}
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? mode === "set-password"
              ? "Setting password…"
              : "Creating account…"
            : mode === "set-password"
              ? "Set password & join"
              : "Create account & join"}
        </Button>

        <p className="text-center text-[11px] text-[var(--color-text-muted)]">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-[var(--color-text-link)] hover:underline"
          >
            Sign in
          </Link>
        </p>
      </form>
    </InviteCard>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function AcceptInvitePage() {
  useBodyClass("auth-scroll");
  const { token } = Route.useSearch();
  const currentUser = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();

  const previewQ = usePreviewInvitation(token);

  // Build the return URL for the "log in first" branch so login redirects back.
  const returnUrl = `/accept-invite?token=${encodeURIComponent(token)}`;

  // ── No token supplied (empty string from default) ──────────────────────────
  // Check this before the loading state: a disabled query (no token) still
  // reports isPending:true in TanStack Query v5 until it fetches once.
  if (token === "") {
    return (
      <InvalidInvite reason="No invitation token was provided. Check the link in your email and try again." />
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (previewQ.isLoading || previewQ.isPending) {
    return <LoadingSkeleton />;
  }

  // ── Network / unexpected error ─────────────────────────────────────────────
  if (previewQ.isError) {
    return (
      <InvalidInvite reason="We couldn't load this invitation. The link may be broken — check your email and try again." />
    );
  }

  const preview = previewQ.data?.data;

  // ── Invalid / expired / revoked ────────────────────────────────────────────
  if (!preview?.valid) {
    const reason =
      preview?.expired === true
        ? "This invitation link has expired. Ask the workspace admin to send a new one."
        : preview?.status === "revoked"
          ? "This invitation has been revoked. Contact the workspace admin if you think this is a mistake."
          : preview?.status === "accepted"
            ? "This invitation has already been accepted."
            : "This invitation link is no longer valid. It may have expired or been revoked.";
    return <InvalidInvite reason={reason} />;
  }

  // ── Valid invitation ───────────────────────────────────────────────────────

  // Logged in: check email match
  if (isAuthenticated && currentUser !== null) {
    const emailMatch =
      currentUser.email.toLowerCase() === (preview.email ?? "").toLowerCase();

    if (emailMatch) {
      return (
        <AcceptAndJoin
          token={token}
          preview={{
            tenantName: preview.tenantName,
            inviterName: preview.inviterName,
            roleName: preview.roleName,
            membershipType: preview.membershipType,
          }}
        />
      );
    }

    // Email mismatch — wrong account
    return (
      <WrongAccount
        inviteEmail={preview.email ?? ""}
        currentEmail={currentUser.email}
      />
    );
  }

  // Not logged in, account exists but has NO password (provisioned employee)
  // → let them set a password and accept in one step. Without this they'd be
  // bounced to a login they can't complete (no password was ever set).
  if (preview.alreadyHasAccount === true && preview.hasPassword === false) {
    return (
      <RegisterAndAcceptForm
        token={token}
        inviteEmail={preview.email ?? ""}
        mode="set-password"
        preview={{
          tenantName: preview.tenantName,
          inviterName: preview.inviterName,
          roleName: preview.roleName,
          membershipType: preview.membershipType,
        }}
      />
    );
  }

  // Not logged in: has existing account WITH a password → prompt to log in
  if (preview.alreadyHasAccount === true) {
    return (
      <LogInFirst
        tenantName={preview.tenantName}
        inviterName={preview.inviterName}
        roleName={preview.roleName}
        membershipType={preview.membershipType}
        returnUrl={returnUrl}
      />
    );
  }

  // Not logged in, no account → register-and-accept form
  return (
    <RegisterAndAcceptForm
      token={token}
      inviteEmail={preview.email ?? ""}
      preview={{
        tenantName: preview.tenantName,
        inviterName: preview.inviterName,
        roleName: preview.roleName,
        membershipType: preview.membershipType,
      }}
    />
  );
}
